/**
 * Otp input component
 *
 * Checklist of required behaviour:
 * 1. match dial with both part : integer and decimals (`m63` : 6 white inputs for integers, 3 black input for decimals).
 * 2. on click, the cursor is by default on the most left empty box,
 * 3. on type, the cursor is automatically shifted to the next box on the right
 * 4. when I add a numbers in between existing numbers:
 * 		4.1. the numbers on the right are shifted to 1 block on the right
 * 		4.2. the cursor stays in the same box
 * 5. when I remove a number between boxes with numbers, the numbers on the right are shifted to 1 block on the left
 * 6. when all boxes are filled with numbers, then I can't add any numbers (I have to delete one or select one and replace it)
 * 7. go to next box on input repeated digit
 *
 * @author Carlos Lopez Rodriguez <clopez@adasasistemas.com>
 * Credits: based on https://github.com/dominicarrojado/react-typescript-otp-input/blob/main/src/components/OtpInput.tsx
 */

// Library
import React, { useMemo } from 'react';

// CSS Style for this module
import styles from './otpInput.module.scss';

const RE_DIGIT = new RegExp(/^\d+$/);

export type OtpInputProps = {
	/**
	 * The value of the otp input as a concatenation of each
	 * character from every individual input value
	 */
	value: string | undefined;
	/**
	 * Required length of the otp input value to display a set of inputs
	 */
	valueLength: number;
	/**
	 * Number of inputs with special style
	 */
	specialInputsNumber?: number;
	/**
	 * String for determining the side of special inputs. Left hand-side as default.
	 */
	specialInputsSide?: 'left' | 'right';
	/**
	 * Default text that is displayed within each input
	 */
	placeholder?: string;
	/**
	 * onChange function for keeping track field value
	 * debounce is built in
	 */
	onChange: (value: string) => void;
	/**
	 * Msg to be displayed when the status is error
	 */
	errorMsg?: string;
	/**
	 * String for determining whether a error should be displayed
	 */
	status?: 'valid' | 'error';
	/**
	 * Custom styles for the container
	 */
	containerStyles?: string;
	/**
	 * Custom styles for regular inputs
	 */
	inputStyles?: string;
	/**
	 * Custom styles for special inputs
	 */
	specialInputStyles?: string;
	/**
	 * Boolean for determining whether this field has to be autofocused
	 */
	autoFocus?: boolean;
	/**
	 * Function to be called when the input loses focus
	 */
	onBlur?: () => void;
};

/**
 * Otp intput let users enter digits individually in a set of inputs
 * @param {OtpInputProps} Prop
 * @returns an styled otp input element
 */
export const OtpInput = ({
	value,
	valueLength,
	specialInputsNumber = 0,
	specialInputsSide = 'left',
	placeholder,
	onChange = () => {
		return;
	},
	errorMsg = 'Please fill up the field',
	status = 'valid',
	containerStyles,
	inputStyles,
	specialInputStyles,
	autoFocus,
	onBlur,
}: OtpInputProps) => {
	const regularInputsNumber = valueLength - specialInputsNumber;

	/**
	 * Initial content values for inputs values
	 */
	const valueItems = useMemo(() => {
		const valueArray = value ? value.split('') : Array.from(Array(valueLength));
		const items: Array<string> = [];

		for (let i = 0; i < valueLength; i++) {
			const char = valueArray[i];

			if (RE_DIGIT.test(char)) {
				items.push(char);
			} else {
				items.push('');
			}
		}

		return items;
	}, [value, valueLength]);

	/**
	 * Check whether an input is categorised as special
	 * @param {number} idx index of input element
	 * @returns {boolean} result
	 */
	const isSpecialInput = (idx: number) => {
		if (specialInputsSide == 'left') {
			return idx + 1 <= specialInputsNumber;
		} else {
			return regularInputsNumber < idx + 1;
		}
	};

	/**
	 * Safe focus on previous input element if there is
	 * @param {HTMLElement} target current active element
	 */
	const focusToPrevInput = (target: HTMLElement) => {
		const previousElementSibling = target.previousElementSibling as HTMLInputElement | null;
		previousElementSibling?.focus();
	};

	/**
	 * Safe focus on next input element if there is
	 * @param {HTMLElement} target current active element
	 */
	const focusToNextInput = (target: HTMLElement) => {
		const nextElementSibling = target.nextElementSibling as HTMLInputElement | null;
		nextElementSibling?.focus();
	};

	/**
	 * On add digit between existing digits, digits on the right are shifted to 1 block on the right
	 * @param {string} targetValue value of current input (two digits as length)
	 * @param {number} idx index of current input element
	 * @returns new final value
	 */
	const addDigitBetween = (targetValue: string, idx: number) => {
		const inputDigits = targetValue.split('');
		const firstDigit = inputDigits[0];
		const secondDigit = inputDigits[1];

		const firstPortion = valueItems.slice(0, idx);
		const secondPortion = valueItems.slice(idx + 1);
		const newValueItems = [...firstPortion, firstDigit, secondDigit, ...secondPortion];
		newValueItems.pop();

		return newValueItems.join('');
	};

	/**
	 * Handle change on input element.
	 * @param event event of content change within an input element
	 * @param {number} idx index of input element
	 */
	const inputOnChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
		const target = e.target;
		const targetValue = target.value.trim();
		const isTargetValueDigit = RE_DIGIT.test(targetValue);

		if (!isTargetValueDigit && targetValue !== '') {
			return;
		}

		const targetValueLength = targetValue.length;
		const isLastInput = idx == valueLength - 1;

		if (targetValueLength === 0 || targetValueLength === 1) {
			const newValue = value
				? value.substring(0, idx) + targetValue + value.substring(idx + 1)
				: targetValue;
			onChange(newValue.trim());

			if (!isTargetValueDigit) {
				return;
			}

			focusToNextInput(target);
		} else if (targetValueLength == 2) {
			// if add digit between existing digits, digits on the right are shifted to 1 block on the right
			const isFullOfDigits = value?.length == valueLength;
			if (!isLastInput && !isFullOfDigits) {
				const newValue = addDigitBetween(targetValue, idx);
				// propagate change for the final value
				onChange(newValue);
			}
		}

		// when all inputs are filled, deactivate focus – note: this is not desired on forms behaviour
		// if (isLastInput) {
		// 	target.blur();
		// }
	};

	/**
	 * Handle any special key down on input element.
	 * Implements same digit overwriting, navigation with arrow keys and backspace key
	 * @param event event of key down within an input element
	 */
	const inputOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		const { key } = e;
		const target = e.target as HTMLInputElement;

		const isSelecting = target?.selectionStart == 0 && target?.selectionEnd == 1;
		const isSameDigit = key == target.value;
		const isOverwritingSameDigit = isSelecting && isSameDigit;

		if (isOverwritingSameDigit || key === 'ArrowRight' || key === 'ArrowDown') {
			e.preventDefault();
			return focusToNextInput(target);
		}

		if (key === 'ArrowLeft' || key === 'ArrowUp') {
			e.preventDefault();
			return focusToPrevInput(target);
		}

		const targetValue = target.value;
		if (e.key !== 'Backspace' || targetValue !== '') {
			return;
		}

		focusToPrevInput(target);
	};

	/**
	 * Handle focus on an input element.
	 * Focus the first empty input and select its value.
	 * @param {event} event event of focus on an input element
	 */
	const inputOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
		const { target } = event;

		// keep focusing back until previous input element has value
		const prevInputEl = target.previousElementSibling as HTMLInputElement | null;
		if (prevInputEl && prevInputEl.value === '') {
			return prevInputEl.focus();
		}

		// select text on focus
		target.setSelectionRange(0, target.value.length);
	};

	return (
		<div>
			<div className={[styles['otp-group'], containerStyles].join(' ')}>
				{valueItems.map((digit, idx) => {
					return (
						<input
							key={idx}
							type="text"
							inputMode="numeric"
							autoComplete="one-time-code"
							pattern="\d{2}"
							placeholder={placeholder}
							className={[
								[styles['otp-input'], inputStyles].join(' '),
								isSpecialInput(idx) && [styles['otp-input--special'], specialInputStyles].join(' '),
								styles[`otp-input-${status}`],
							].join(' ')}
							value={digit}
							onChange={e => inputOnChange(e, idx)}
							onKeyDown={inputOnKeyDown}
							onFocus={inputOnFocus}
							autoFocus={autoFocus}
							onBlur={onBlur}
						/>
					);
				})}
			</div>
			<p
				className={[styles['error-msg'], status !== 'error' && styles['error-msg-hide']].join(' ')}
			>
				{errorMsg}
			</p>
		</div>
	);
};
